import React, { useMemo, useState } from 'react';
import * as Cons from 'data/Consignment';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import CurrencyInput from 'components/UI/CurrencyInput';
import ConfirmationModal from 'components/UI/ConfimationModal';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { updateAdditionalShippingCost } from 'requests/consignment';
import { useCountries } from 'hooks/useCountries';
import { getCurrencyIsoByCountry, getCurrencySymbol } from 'utils/country';
import { TEXT_LABELS } from 'constants/order';
import { GENERAL_CONSTANTS } from 'constants/general';
import { PermissionCode } from 'data/UserPermissions';
import { Main, Text, Border } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';

interface Props {
  consignmentDetails: Cons.ConsignmentDetails;
  onActionComplete: () => void;
}

/**
 * "Additional shipping cost" row inside the AWAITS_SHIPPING_APPROVAL banner
 * (`OrderDetails.tsx`'s `consignmentShippingCostApproval`). Read-only by default; Edit
 * turns the amount into an inline input with no modal (AC2). Save reuses the same
 * PUT /additionalShippingCost endpoint as Remove — the backend tells them apart purely by
 * whether the submitted amount is zero.
 */
const AdditionalShippingCostApproval = ({ consignmentDetails, onActionComplete }: Props) => {
  const { countries } = useCountries();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState<string>(consignmentDetails.totalAdditionalShippingCost.toFixed(2));
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const { consignmentId, totalAdditionalShippingCost } = consignmentDetails;

  const currencyIso = useMemo(() => {
    const countryIso = countries.find(country => country.id === consignmentDetails.countryId)?.countryIso;

    return getCurrencyIsoByCountry(countryIso!);
  }, [countries, consignmentDetails.countryId]);

  const currencySymbol = getCurrencySymbol(currencyIso);

  const updateReq = useAPI({
    deferFn: updateAdditionalShippingCost,
    onResolve: (result: Cons.ConsignmentDetails) => {
      const isRemoval = result.totalAdditionalShippingCost === 0;

      renderToast(toast.TYPE.SUCCESS, isRemoval ? TEXT_LABELS.REMOVE_SHIPPING_COST_SUCCESS : TEXT_LABELS.SHIPPING_COST_UPDATE_SUCCESS);
      setEditing(false);
      onActionComplete();
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.SHIPPING_COST_UPDATE_FAILED);
    }
  });

  const isLoading = updateReq.isLoading;
  const parsedAmount = parseFloat(amount);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount > 0;
  const isAmountChanged = parsedAmount !== totalAdditionalShippingCost;
  const canSave = isValidAmount && isAmountChanged && !isLoading;

  const startEditing = () => {
    setAmount(totalAdditionalShippingCost.toFixed(2));
    setEditing(true);
  };

  const cancelEditing = () => {
    setAmount(totalAdditionalShippingCost.toFixed(2));
    setEditing(false);
  };

  const save = () => {
    if (!canSave) return;
    updateReq.run(consignmentId, { totalAdditionalShippingCost: parsedAmount.toFixed(2) });
  };

  const confirmRemove = () => {
    setShowRemoveConfirm(false);
    updateReq.run(consignmentId, { totalAdditionalShippingCost: '0' });
  };

  return (
    <FlexboxContainer flexDirection="column">
      <FlexboxContainer alignItems="center" gap={Space.spBasePlus}>
        {editing ? (
          <CurrencyInput
            id="additional-shipping-cost-input"
            labelName=""
            width="160px"
            prefix={currencySymbol}
            decimalsLimit={2}
            value={amount}
            disabled={isLoading}
            onValueChange={(value?: string) => setAmount(value || '')}
            placeholder="New shipping cost"
          />
        ) : (
          <span style={{ fontSize: '24px', fontWeight: 'bold', color: Text.primary }}>
            {`${currencySymbol}${totalAdditionalShippingCost.toFixed(2)}`}
          </span>
        )}

        {editing ? (
          <Button
            isGhost
            color={Main.accent}
            disabled={!canSave}
            permissionCode={PermissionCode.ADDITIONAL_SHIPPING_COSTS}
            onClick={save}
          >
            {TEXT_LABELS.SAVE_AND_RESEND_FOR_APPROVAL}
          </Button>
        ) : (
          <Button
            isGhost
            color={Main.accent}
            disabled={isLoading}
            permissionCode={PermissionCode.ADDITIONAL_SHIPPING_COSTS}
            onClick={startEditing}
          >
            {TEXT_LABELS.EDIT_SHIPPING_COST}
          </Button>
        )}

        {editing && (
          <Button
            isGhost
            borderColor={Border.main}
            color={Text.primary}
            disabled={isLoading}
            onClick={cancelEditing}
          >
            {GENERAL_CONSTANTS.CANCEL}
          </Button>
        )}

        <Button
          isGhost
          theme="error"
          color={Main.error}
          borderColor={Main.error}
          disabled={isLoading}
          permissionCode={PermissionCode.ADDITIONAL_SHIPPING_COSTS}
          onClick={() => setShowRemoveConfirm(true)}
        >
          {TEXT_LABELS.REMOVE_SHIPPING_COST}
        </Button>
      </FlexboxContainer>

      <FlexboxContainer margin={`${Space.spBasePlus} 0 0`}>
        <span style={{ fontSize: '14px', color: Text.secondary }}>
          {TEXT_LABELS.SHIPPING_COST_HELPER_TEXT}
        </span>
      </FlexboxContainer>

      <ConfirmationModal
        show={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={confirmRemove}
        title={TEXT_LABELS.REMOVE_SHIPPING_COST_CONFIRM_TITLE}
        message={TEXT_LABELS.REMOVE_SHIPPING_COST_CONFIRM_MESSAGE}
        confirmText={TEXT_LABELS.REMOVE_SHIPPING_COST_CONFIRM_ACTION}
        primaryButtonColor={Main.error}
        disabled={isLoading}
      />
    </FlexboxContainer>
  );
};

export default AdditionalShippingCostApproval;
